import { Segment, Grid } from "semantic-ui-react";
import PermitTypesGrid from "./PermitTypesGrid";
import { PermitType } from "../models/PermitType";
import { OrgRole } from "../models/OrgRole";
import { DocumentType } from "../models/DocumentType";
import { useEffect, useState } from "react";
import PermitTypeViewContainer from "../containers/PermitTypeViewContainer";
import EditPermitTemplateModal from "./EditPermitTemplateModal";
import { AsyncActionStatus } from "../models/AsyncActionStatus";
import { toastSuccess } from "../utils";
import { useGeneralPolicies } from "../hooks/useGeneralPolicies";

interface Props {
  organizationId: string;
  permitTypes?: PermitType[] | null;
  allOrgRoles?: OrgRole[];
  allDocumentTypes?: DocumentType[];
  onEdit: (organizationId: string, permitTypeId: string, name: string, templateJson?: string) => void;
  editStatus: AsyncActionStatus;
}

const PermitTypesList = ({ organizationId, permitTypes, allOrgRoles, allDocumentTypes, onEdit, editStatus }: Props) => {
  const { authorizedSystemAdmin } = useGeneralPolicies();
  const [openViewPermitTypeSection, setOpenViewPermitTypeSection] = useState(false);
  const [selectedPermitTypeToView, setSelectedPermitTypeToView] = useState<PermitType | undefined>(undefined);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedPermitTypeToEdit, setSelectedPermitTypeToEdit] = useState<PermitType | undefined>(undefined);

  const handlePermitTypeClicked = (permitType: PermitType) => {
    setOpenViewPermitTypeSection(true);
    setSelectedPermitTypeToView(permitType);
  };

  const handleViewPermitTypeSectionClose = () => {
    setSelectedPermitTypeToView(undefined);
    setOpenViewPermitTypeSection(false);
  };

  const handleEditClicked = (permitTypeId: string) => {
    const permitType = permitTypes?.find((p) => p.id === permitTypeId);
    if (permitType) {
      setSelectedPermitTypeToEdit({ ...permitType });
      setOpenEditModal(true);
    }
  };

  const handleEditModalClose = () => {
    setOpenEditModal(false);
    setSelectedPermitTypeToEdit(undefined);
    editStatus.reset();
  };

  const handleEditSubmit = (name: string, id: string, templateJson?: string) => {
    onEdit(organizationId, id, name, templateJson);
  };

  useEffect(() => {
    if (editStatus.successful) {
      toastSuccess("Permit type updated successfully!");
      setOpenEditModal(false);
      setSelectedPermitTypeToEdit(undefined);
    }
  }, [editStatus.successful]);

  return (
    <>
      <Segment basic loading={false}>
        <Grid columns={openViewPermitTypeSection ? "2" : "1"}>
          <Grid.Row>
            <Grid.Column width={openViewPermitTypeSection ? "10" : "16"}>
              <PermitTypesGrid
                permitTypes={permitTypes}
                selectedPermitType={selectedPermitTypeToView}
                onPermitTypeClicked={handlePermitTypeClicked}
                onEditPermitType={handleEditClicked}
                showEditAction={authorizedSystemAdmin}
              />
            </Grid.Column>
            {openViewPermitTypeSection && (
              <Grid.Column width="6">
                <PermitTypeViewContainer
                  onClose={handleViewPermitTypeSectionClose}
                  permitTypeId={selectedPermitTypeToView?.id}
                  organizationId={organizationId}
                  allOrgRoles={allOrgRoles ?? []}
                  allDocumentTypes={allDocumentTypes ?? []}
                />
              </Grid.Column>
            )}
          </Grid.Row>
        </Grid>
      </Segment>

      {selectedPermitTypeToEdit && (
        <EditPermitTemplateModal
          open={openEditModal}
          onClose={handleEditModalClose}
          onSubmit={handleEditSubmit}
          submitInProgress={editStatus.loading}
          submitErrors={editStatus.errors}
          entity={selectedPermitTypeToEdit}
          entityLabel="Permit Type"
        />
      )}
    </>
  );
};

export default PermitTypesList;
